import { useEffect, useState } from "react";
import { ChronoUnit, DateTimeFormatter, Duration, LocalDate, LocalDateTime } from "@js-joda/core";
import { Locale } from "@js-joda/locale_en-us";
import { Container, Row, Col, Card, Button as BsButton } from "react-bootstrap";
import { Clock, FileText, PSquareFill, SignpostSplitFill } from "react-bootstrap-icons";
import { useLoaderData, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { PageTitle } from "./Controls/PageTitle";
import { getReservationStatus, ReservationStatus } from "./Controls/ReservationCard";
import { facilityImg } from "./Data/Common";
import { showDuration, showDurationShort, showTime, showTimeShort } from "./Data/Date";
import { showFacilityAddress } from "./Data/Location";
import { showMoney } from "./Data/Money";
import { Reservation } from "./Data/ApiTypes";

const STATUS_CONFIG: Record<ReservationStatus, { label: string; dotClass: string }> = {
    active:   { label: 'In Progress', dotClass: 'bg-success'   },
    upcoming: { label: 'Upcoming',    dotClass: 'bg-primary'   },
    expired:  { label: 'Expired',     dotClass: 'bg-secondary' },
};

const shortMonthDay = DateTimeFormatter.ofPattern("MMM d").withLocale(Locale.US);

function showDateLabel(d: LocalDate): string {
    if (d.equals(LocalDate.now())) return `Today, ${d.format(shortMonthDay)}`;
    return d.format(shortMonthDay);
}

function useMinuteTick() {
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick(t => t + 1), 60_000);
        return () => clearInterval(id);
    }, []);
}

function ProgressCard({ start, end }: { start: LocalDateTime; end: LocalDateTime }) {
    useMinuteTick();
    const now = LocalDateTime.now();
    const totalMinutes   = start.until(end, ChronoUnit.MINUTES);
    const elapsedMinutes = start.until(now, ChronoUnit.MINUTES);
    const remainingMin   = Math.max(0, now.until(end, ChronoUnit.MINUTES));
    const pct = totalMinutes > 0
        ? Math.min(100, Math.max(0, (elapsedMinutes / totalMinutes) * 100))
        : 0;

    return (
        <Card className="mb-3">
            <Card.Body>
                <div className="d-flex justify-content-between mb-2">
                    <span className="fw-semibold small">Time elapsed</span>
                    <span className="fw-semibold small text-dark">
                        {showDurationShort(Duration.ofMinutes(remainingMin))} remaining
                    </span>
                </div>
                <div className="progress mb-2" style={{ height: 8, borderRadius: 4 }}>
                    <div className="progress-bar bg-dark" style={{ width: `${pct}%`, borderRadius: 4 }} />
                </div>
                <div className="d-flex justify-content-between text-muted small">
                    <span>{showTimeShort(start.toLocalTime())}</span>
                    <span>Now &middot; {showTimeShort(now.toLocalTime())}</span>
                    <span>{showTimeShort(end.toLocalTime())}</span>
                </div>
            </Card.Body>
        </Card>
    );
}

export function UserReservationDetailsPage() {
    const nav     = useNavigate();
    const reserve = useLoaderData() as Reservation;
    const f       = reserve.facility;
    const status  = getReservationStatus(reserve);
    const { label, dotClass } = STATUS_CONFIG[status];

    const startDate = reserve.start.toLocalDate();
    const confirmationNum = `${reserve.confirmationNum}`;

    return (
        <Container fluid>
            <Row>
                <Col>
                    <BsButton variant="link" className="p-0" onClick={() => nav('/user/reservation/upcoming')}>← Back</BsButton>
                    <PageTitle className="mb-1">Reservation</PageTitle>

                    <span className="d-inline-flex align-items-center gap-2 rounded-pill px-2 py-1 small mb-3"
                          style={{ color: 'var(--bs-body-color)' }}>
                        <span className={`rounded-circle ${dotClass}`} style={{ width: 8, height: 8 }} aria-hidden />
                        {label}
                    </span>

                    {/* Hero image */}
                    <div className="position-relative rounded-3 overflow-hidden mb-3" style={{ height: 200 }}>
                        <img src={facilityImg(f.listImageId)} alt={f.name}
                             style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <div className="position-absolute bottom-0 start-0 end-0 p-3"
                             style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}>
                            <div className="fw-bold text-white fs-5">{f.name}</div>
                            <div className="text-white-50 small">
                                {f.address.addressLine1}, {f.address.city}, {f.address.state} {f.address.zipCode}
                            </div>
                        </div>
                        <div className="position-absolute top-0 end-0 m-2">
                            <span className="rounded-circle bg-white bg-opacity-75 d-inline-flex align-items-center justify-content-center"
                                  style={{ width: 32, height: 32 }}>
                                <PSquareFill size={16} className="text-primary" />
                            </span>
                        </div>
                    </div>

                    {/* Check-in / Check-out */}
                    <Card className="mb-3">
                        <Card.Body className="d-flex p-0">
                            <div className="flex-fill text-center py-3 border-end">
                                <div className="text-muted small text-uppercase fw-semibold">Check-In</div>
                                <div className="fw-bold fs-4">{showTime(reserve.start.toLocalTime())}</div>
                                <div className="text-muted small">{showDateLabel(reserve.start.toLocalDate())}</div>
                            </div>
                            <div className="flex-fill text-center py-3">
                                <div className="text-muted small text-uppercase fw-semibold">Check-Out</div>
                                <div className="fw-bold fs-4">{showTime(reserve.end.toLocalTime())}</div>
                                <div className="text-muted small">{showDateLabel(reserve.end.toLocalDate())}</div>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Duration & price */}
                    <Card className="mb-3">
                        <Card.Body className="d-flex align-items-center justify-content-between">
                            <span className="d-flex align-items-center gap-2">
                                <Clock size={16} className="text-muted" />
                                {showDuration(reserve.duration)}
                            </span>
                            <span>
                                <span className="fw-bold">{showMoney(reserve.price)}</span>
                                <span className="text-muted ms-1">total</span>
                            </span>
                        </Card.Body>
                    </Card>

                    {/* Progress bar (active only) */}
                    {status === 'active' && <ProgressCard start={reserve.start} end={reserve.end} />}

                    <Card className="mb-3 rounded-3 shadow-subtle overflow-hidden">
                        <div className="d-flex align-items-center gap-3 px-3 py-3 border-bottom">
                            <div className="rounded-3 flex-shrink-0 d-flex align-items-center justify-content-center text-white"
                                 style={{ width: 40, height: 40, backgroundColor: 'var(--color-mid)' }}>
                                <SignpostSplitFill />
                            </div>
                            <div className="flex-grow-1">
                                <div className="text-muted small text-uppercase">Location</div>
                                <div className="fw-semibold">{f.address.addressLine1}</div>
                                <div className="text-muted small">{f.address.city}, {f.address.state} {f.address.zipCode}</div>
                            </div>
                            <BsButton variant="outline-secondary" size="sm"
                                      className="rounded-pill d-flex align-items-center gap-1 flex-shrink-0"
                                      onClick={() => window.open(`http://maps.apple.com/?q=${encodeURIComponent(showFacilityAddress(f.address))}`)}>
                                <SignpostSplitFill className="small" /> Directions
                            </BsButton>
                        </div>

                        <div className="d-flex align-items-center gap-3 px-3 py-3"
                             style={{ cursor: 'pointer' }}
                             onClick={() => { navigator.clipboard.writeText(confirmationNum); toast.success("Confirmation number copied"); }}>
                            <div className="rounded-3 flex-shrink-0 d-flex align-items-center justify-content-center text-white"
                                 style={{ width: 40, height: 40, backgroundColor: 'var(--color-mid)' }}>
                                <FileText />
                            </div>
                            <div className="flex-grow-1">
                                <div className="text-muted small text-uppercase">Confirmation</div>
                                <div className="fw-semibold">{confirmationNum}</div>
                                <div className="text-muted small">Tap to copy</div>
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}
